/** @jsxImportSource @emotion/react */
import * as React from 'react';
import { css, SerializedStyles } from '@emotion/react';
import checkImg from '../../../assets/icons/checkCircleFilled.svg';
import xCircleImg from '../../../assets/icons/xCircleRed.svg';
import { BigNeutral } from '../../../forms/elements/buttons';
import LabeledData from '../../../forms/elements/LabeledData';
import { COLUMN_SPACING } from '../../../forms/styles';
import { sortByDateProp, sortByProp } from '../../../language/module';
import { formatDate } from '../../../language/time';
import { Column } from '../../../layout/flexbox';
import { ColumnConfig } from '../../../layout/table/columnConfig';
import Table from '../../../layout/table/Table';
import { isExpired, isSigned, SIGNABLE_WAIVERS } from './module';
import { Waiver } from './waiver';
import { getResource } from '../../../language/resources/getResource';


const styles = {
  tableData: css`
    line-height: 48px !important;
    vertical-align: center !important;
  `,
  isSignedIcon: css`
    width: 38px; 
    height: 38px; 
    display: 
    block; margin: auto;
  `
};




type Props = {
  guestName: string;
  isLoading: boolean;
  data: Waiver[];
  error?: string | undefined;
  onSignClick: (waiver: Waiver) => void;
  rootCss?: SerializedStyles;
  resources: Record<string, any>;
  dateFormat: string;
};

/**
 * WaiverTable: a component that renders a list of waivers in table form including the name of the waiver, the signature
 * status of a waiver, when a waiver was signed, and whether or not it's expired. When the sign button is clicked, calls
 * `props.onSignClick`.
 *
 * PureComponent: true
 * Connected to Redux: false
 */
export default class WaiverTable extends React.PureComponent<Props> {
  private makeClickToSignHandler = (waiver: Waiver) => () => this.props.onSignClick(waiver);

  private columnConfig: Array<ColumnConfig<Waiver>> = [
    {
      id: 1, 
      title:  getResource(this.props.resources, 'WAIVER_TABLE_COLUMN_WAIVER'),
      onSort: sortByProp('title', false),
    },
    {
      id: 2, 
      title:  getResource(this.props.resources, 'WAIVER_TABLE_COLUMN_WAIVER_SIGNED'),
      onSort: sortByProp('status', false),
    },
    {
      id: 3, 
      title:                   getResource(this.props.resources, 'WAIVER_TABLE_COLUMN_DATE_SIGNED'),
      onSort:                  sortByDateProp('signedDate'),
      defaultSortingColumn:    true,
      defaultSortingDirection: 'desc',
    },
  ];


  public renderWaiverStatus = (signed, expired, waiver: Waiver): React.ReactNode => {
    if (waiver.enableSignature && !signed) {
      return <BigNeutral
        onClick={this.makeClickToSignHandler(waiver)}
        css={css`width: 100%;`}
      >
       {getResource(this.props.resources, 'WAIVER_TABLE_CLICK_TO_SIGN')}
      </BigNeutral>;
    }

    const signedTxt = signed ? formatDate(waiver.signedDate, this.props.dateFormat) || getResource(this.props.resources, 'WAIVER_TABLE_UNKNOWN') : getResource(this.props.resources, 'WAIVER_TABLE_UNSIGNED');

    return `${signedTxt} ${expired ? getResource(this.props.resources, 'WAIVER_TABLE_EXPIRED', formatDate(waiver.expirationDate, this.props.dateFormat)) : ''}`;
  };


  public renderRow = (waiver: Waiver): React.ReactNode => {
    const expired = isExpired(waiver);
    const signed  = isSigned(waiver);

    return <tr key={waiver.version}>
      <td css={styles.tableData}>{waiver.title}</td>
      <td
        css={css`
          width: 150px;
          text-align: center;
          ${styles.tableData};
        `}
      >
        <img
          title={isSigned(waiver) ? getResource(this.props.resources, 'WAIVER_TABLE_SIGNED') : getResource(this.props.resources, 'WAIVER_TABLE_NOT_SIGNED')}
          src={isSigned(waiver) ? checkImg : xCircleImg}
          alt={isSigned(waiver) ? '✓' : 'X'}
          css={styles.isSignedIcon}
        />
      </td>
      <td
        css={css`
          width: 210px;
          text-align: ${signed || expired || !SIGNABLE_WAIVERS.includes(waiver.version) ? 'left' : 'center'};
          ${styles.tableData};
        `}
      >
        {this.renderWaiverStatus(signed, expired, waiver)}
      </td>
    </tr>;
  };


  public render = () => (
    <Column
      rootCss={css`
        ${COLUMN_SPACING};
        ${this.props.rootCss};
      `}
    >
      <LabeledData label={getResource(this.props.resources, 'WAIVER_TABLE_GUEST_LABEL')} data={this.props.guestName} />

      <Table
        columns={this.columnConfig}
        data={this.props.data}
        error={this.props.error}
        isLoading={this.props.isLoading}
        noDataMessage={getResource(this.props.resources, 'WAIVER_TABLE_NO_WAIVERS')}
        renderRow={this.renderRow}
      />
    </Column>
  );
}
